import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, Shield, ShieldCheck, UserCog, Loader2, X, Check, Search, KeyRound, Scale, Pencil, Power, Briefcase, Wrench, Calculator } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const ROLES = [
  { value: "admin", label: "Administrator", icon: ShieldCheck,
    perms: "Full access. Manages users & permissions, all pipelines, properties and maintenance." },
  { value: "power_user", label: "Power User", icon: UserCog,
    perms: "Can create, edit and delete deals & properties across all pipelines." },
  { value: "client", label: "Client", icon: Shield,
    perms: "View-only pipelines & properties (assigned to them); can raise maintenance requests." },
  { value: "legal_rep", label: "Legal Rep", icon: Scale,
    perms: "Can only view identity documents in a profile. No pipeline or property access." },
  { value: "accountant", label: "Accountant", icon: Calculator,
    perms: "Same as Legal Rep: allocated Sale/Purchase/Tenancy pipelines, client files, request signatures and raise invoices." },
  { value: "broker", label: "Broker", icon: Briefcase,
    perms: "Allocated pipelines only. Property info read-only (no mortgage/valuation). Can upload documents but only see document lists." },
  { value: "caretaker", label: "Caretaker", icon: Wrench,
    perms: "Maintenance pipeline only. Property info read-only (no mortgage/utility/valuation). Can upload maintenance documents." },
];
const roleMeta = (r) => ROLES.find((x) => x.value === r) || ROLES[2];
const roleBadge = {
  admin: "text-emerald-700 bg-emerald-50 border-emerald-200",
  power_user: "text-blue-700 bg-blue-50 border-blue-200",
  client: "text-slate-600 bg-slate-100 border-slate-200",
  legal_rep: "text-amber-700 bg-amber-50 border-amber-200",
  accountant: "text-sky-700 bg-sky-50 border-sky-200",
  broker: "text-violet-700 bg-violet-50 border-violet-200",
  caretaker: "text-teal-700 bg-teal-50 border-teal-200",
};
const INPUT = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [resetId, setResetId] = useState(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sendReset = async (u) => {
    setResetId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(data.message || `Reset link sent to ${u.email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reset link");
    } finally { setResetId(null); }
  };

  const changeRole = async (u, role) => {
    if (u.role === role) return;
    setSavingId(u.id);
    const prev = users;
    setUsers(users.map((x) => (x.id === u.id ? { ...x, role } : x)));
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${data.name} is now ${roleMeta(role).label}`);
    } catch (e) {
      setUsers(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update role");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (u) => {
    const prev = users;
    setUsers(users.filter((x) => x.id !== u.id));
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User removed"); }
    catch (e) { setUsers(prev); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete user"); }
  };

  const toggleActive = async (u) => {
    const next = !(u.active ?? true);
    setSavingId(u.id);
    const prev = users;
    setUsers(users.map((x) => (x.id === u.id ? { ...x, active: next } : x)));
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { active: next });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${data.name} is now ${data.active ? "active" : "inactive"}`);
    } catch (e) {
      setUsers(prev);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status");
    } finally { setSavingId(null); }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Administration</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">Create accounts and control permission levels across the portal.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-button" className={`${BTN} px-5 py-2.5`}>
          <Plus className="h-4 w-4" /> Add User
        </button>
      </div>

      {/* Permission legend */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {ROLES.map((r) => (
          <div key={r.value} className="p-5 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="flex items-center gap-2">
              <span className={`h-9 w-9 rounded-lg flex items-center justify-center border ${roleBadge[r.value]}`}>
                <r.icon className="h-4.5 w-4.5" />
              </span>
              <span className="font-semibold text-slate-900">{r.label}</span>
            </div>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">{r.perms}</p>
          </div>
        ))}
      </div>

      {/* Search & filter */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white border border-slate-300 rounded-lg px-3 flex-1 min-w-[220px] focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500/20">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="user-search-input" placeholder="Search by name or email…" value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent py-2.5 text-sm text-slate-900 focus:outline-none" />
        </div>
        <select data-testid="user-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
          <option value="all">All roles</option>
          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-6 py-4 font-medium">User</th>
                  <th className="px-6 py-4 font-medium">Email</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Permission Level</th>
                  <th className="px-6 py-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter((u) => roleFilter === "all" || u.role === roleFilter)
                  .filter((u) => {
                    const q = query.trim().toLowerCase();
                    return !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
                  })
                  .map((u) => {
                  const isSelf = u.id === user?.id;
                  return (
                    <tr key={u.id} data-testid="user-row" className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <span className="h-9 w-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700 font-semibold">
                            {(u.name || "?").charAt(0)}
                          </span>
                          <div>
                            <div className="font-medium text-slate-900 flex items-center gap-2">
                              {u.name}
                              {isSelf && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">You</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-500">{u.email}</td>
                      <td className="px-6 py-4">
                        <button onClick={() => toggleActive(u)} disabled={savingId === u.id || isSelf} data-testid="user-active-toggle"
                          title={isSelf ? "You cannot change your own status" : "Toggle whether this user can log in"}
                          className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${(u.active ?? true) ? "text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100" : "text-slate-500 bg-slate-100 border-slate-200 hover:bg-slate-200"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${(u.active ?? true) ? "bg-emerald-500" : "bg-slate-400"}`} />
                          {(u.active ?? true) ? "Active" : "Inactive"}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <select
                            data-testid="user-role-select"
                            value={u.role}
                            disabled={savingId === u.id}
                            onChange={(e) => changeRole(u, e.target.value)}
                            className={`border rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${roleBadge[u.role]}`}>
                            {ROLES.map((r) => <option key={r.value} value={r.value} className="bg-white text-slate-900">{r.label}</option>)}
                          </select>
                          {savingId === u.id && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setEditing(u)}
                            data-testid="edit-user-button"
                            title="Edit name & email"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors">
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => sendReset(u)}
                            disabled={resetId === u.id}
                            data-testid="send-reset-button"
                            title="Email a password reset link to this user"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 transition-colors">
                            {resetId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} Send reset
                          </button>
                          <button
                            onClick={() => remove(u)}
                            disabled={isSelf}
                            data-testid="delete-user-button"
                            title={isSelf ? "You cannot delete your own account" : "Delete user"}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onAdded={(u) => { setUsers([...users, u]); setShowAdd(false); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={(u) => { setUsers((list) => list.map((x) => (x.id === u.id ? u : x))); setEditing(null); }} />}
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.id}`, { name: form.name, email: form.email.toLowerCase() });
      toast.success("User updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><Pencil className="h-5 w-5 text-emerald-600" /><h2 className="text-lg font-semibold text-slate-900">Edit User</h2></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <In label="Full name" testid="edit-user-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <In label="Email" type="email" testid="edit-user-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <button type="submit" disabled={busy} data-testid="save-edit-user-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Changes
          </button>
        </form>
      </div>
    </div>
  );
}

function AddUserModal({ onClose, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success("User created");
      onAdded(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="add-user-modal">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2"><Users className="h-5 w-5 text-emerald-600" /><h2 className="text-lg font-semibold text-slate-900">Add User</h2></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <In label="Full name" testid="new-user-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <In label="Email" type="email" testid="new-user-email-input" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <In label="Temporary password" type="password" testid="new-user-password-input" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />
          <div>
            <label className="text-sm text-slate-600">Permission level</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role-select"
              className="mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="mt-2 text-xs text-slate-500">{roleMeta(form.role).perms}</p>
          </div>
          <button type="submit" disabled={busy} data-testid="save-user-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create User
          </button>
        </form>
      </div>
    </div>
  );
}

const In = ({ label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label className="text-sm text-slate-600">{label}</label>
    <input type={type} required={required} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} />
  </div>
);
